import { FONT_FAMILY } from '../constants/typography';

const FALLBACK_CHAR_W = 7.2;
const DEFAULT_LINE_H = 17;

let measureCtx: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx;
  if (typeof document === 'undefined') {
    measureCtx = null;
    return null;
  }
  const canvas = document.createElement('canvas');
  measureCtx = canvas.getContext('2d');
  return measureCtx;
}

function measureWidth(text: string, font: string): number {
  const ctx = getMeasureContext();
  if (ctx) {
    ctx.font = font;
    const measured = ctx.measureText(text).width;
    return measured > 0 ? measured : text.length * FALLBACK_CHAR_W;
  }
  return Math.max(text.length, 1) * FALLBACK_CHAR_W;
}

export function measureLabelSize(
  text: string,
  font = `12px ${FONT_FAMILY}`,
  lineHeight = DEFAULT_LINE_H,
): { width: number; height: number } {
  const lines = text.length > 0 ? text.split('\n') : [''];
  let width = 0;
  for (const line of lines) {
    width = Math.max(width, measureWidth(line, font));
  }
  return { width, height: Math.max(lines.length, 1) * lineHeight };
}

/** Word-wrap `text` to `maxWidth` and return the box it needs (Konva-style wrap="word"). */
export function measureWrappedText(
  text: string,
  maxWidth: number,
  font = `12px ${FONT_FAMILY}`,
  lineHeight = DEFAULT_LINE_H,
): { width: number; height: number; lines: number } {
  const paragraphs = text.length > 0 ? text.split('\n') : [''];
  const wrapped: string[] = [];
  for (const paragraph of paragraphs) {
    if (!paragraph) {
      wrapped.push('');
      continue;
    }
    const words = paragraph.split(/\s+/).filter(Boolean);
    let current = '';
    for (const word of words) {
      const trial = current ? `${current} ${word}` : word;
      if (measureWidth(trial, font) <= maxWidth || !current) {
        current = trial;
      } else {
        wrapped.push(current);
        current = word;
      }
    }
    if (current) wrapped.push(current);
  }
  const lines = Math.max(wrapped.length, 1);
  let width = 0;
  for (const line of wrapped) {
    width = Math.max(width, measureWidth(line, font));
  }
  return {
    width: Math.min(Math.max(width, 0), Math.max(maxWidth, 0)),
    height: lines * lineHeight,
    lines,
  };
}

export function longestWordWidth(text: string, font = `12px ${FONT_FAMILY}`): number {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;
  return Math.max(...words.map(word => measureWidth(word, font)));
}
